import { API_URL, getAuthHeaders, handleApiError } from "../api/api";
import { demoService } from "../demo/demoService";

export interface UserSettings {
  // Application Settings
  default_transaction_days: number;
  currency: string;
  date_format: string;
  timezone: string;

  // UI Settings
  theme: "light" | "dark" | "auto";
  language: string;
  compact_mode: boolean;
  expenses_future_months_count: number;

  // Data Settings
  auto_backup: boolean;
  data_retention_days: number;

  // Security Settings
  session_timeout: number;
  require_confirmation: boolean;
  discoverable: boolean;

  // Tax Category Settings
  federal_tax_categories: string;
  state_tax_categories: string;
  custom_transaction_categories?: string[];
  charts_layout?: unknown[];
  wife_value_settings?: unknown;
  household_tasks_tool_settings?: unknown;

  // Plaid Configuration
  plaid_environment: "sandbox" | "production";

  // Feature Flags
  enable_ai_categorization: boolean;
  enable_smart_notifications: boolean;
  enable_auto_sync: boolean;
}

const defaultSettings: UserSettings = {
  default_transaction_days: 90,
  currency: "USD",
  date_format: "MM/DD/YYYY",
  timezone: "America/Los_Angeles",
  theme: "light",
  language: "en",
  compact_mode: false,
  expenses_future_months_count: 0,
  auto_backup: true,
  data_retention_days: 365,
  session_timeout: 30,
  require_confirmation: true,
  discoverable: false,
  federal_tax_categories:
    "Business Income,Business Expense,Home Office,Travel,Meals,Equipment,Supplies,Marketing,Professional Services,Software Subscriptions,Insurance",
  state_tax_categories:
    "State Income,State Deductions,State Business Expense,State Travel,State Equipment,Professional License,State Tax Payments,Local Tax,Property Tax,Sales Tax",
  custom_transaction_categories: [],
  charts_layout: [],
  wife_value_settings: {},
  household_tasks_tool_settings: {},
  plaid_environment: "production",
  enable_ai_categorization: true,
  enable_smart_notifications: true,
  enable_auto_sync: true,
};

class SettingsService {
  /**
   * Get user settings from Supabase
   * Returns default settings if user has no saved settings
   */
  async getUserSettings(): Promise<UserSettings> {
    try {
      if (demoService.isDemo()) {
        return defaultSettings;
      }
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        console.warn("No authenticated user, returning default settings");
        return defaultSettings;
      }

      const response = await fetch(`${API_URL}/user-settings`, {
        method: "GET",
        headers,
      });

      if (response.status === 404) {
        console.log("No settings found for user, returning defaults");
        return defaultSettings;
      }

      if (!response.ok) {
        await handleApiError(response);
        return defaultSettings;
      }

      const data = await response.json();

      // Map database fields to our interface
      const settings: UserSettings = {
        default_transaction_days:
          data.default_transaction_days ??
          defaultSettings.default_transaction_days,
        currency: data.currency ?? defaultSettings.currency,
        date_format: data.date_format ?? defaultSettings.date_format,
        timezone: data.timezone ?? defaultSettings.timezone,
        theme: data.theme ?? defaultSettings.theme,
        language: data.language ?? defaultSettings.language,
        compact_mode: data.compact_mode ?? defaultSettings.compact_mode,
        expenses_future_months_count:
          data.expenses_future_months_count ??
          defaultSettings.expenses_future_months_count,
        auto_backup: data.auto_backup ?? defaultSettings.auto_backup,
        data_retention_days:
          data.data_retention_days ?? defaultSettings.data_retention_days,
        session_timeout:
          data.session_timeout ?? defaultSettings.session_timeout,
        require_confirmation:
          data.require_confirmation ?? defaultSettings.require_confirmation,
        discoverable: data.discoverable ?? defaultSettings.discoverable,
        federal_tax_categories:
          data.federal_tax_categories ?? defaultSettings.federal_tax_categories,
        state_tax_categories:
          data.state_tax_categories ?? defaultSettings.state_tax_categories,
        custom_transaction_categories: Array.isArray(
          data.custom_transaction_categories
        )
          ? data.custom_transaction_categories
          : defaultSettings.custom_transaction_categories,
        charts_layout: Array.isArray(data.charts_layout)
          ? data.charts_layout
          : defaultSettings.charts_layout,
        wife_value_settings:
          data.wife_value_settings ?? defaultSettings.wife_value_settings,
        household_tasks_tool_settings:
          data.household_tasks_tool_settings ??
          defaultSettings.household_tasks_tool_settings,
        plaid_environment:
          data.plaid_environment ?? defaultSettings.plaid_environment,
        enable_ai_categorization:
          data.enable_ai_categorization ??
          defaultSettings.enable_ai_categorization,
        enable_smart_notifications:
          data.enable_smart_notifications ??
          defaultSettings.enable_smart_notifications,
        enable_auto_sync:
          data.enable_auto_sync ?? defaultSettings.enable_auto_sync,
      };

      return settings;
    } catch (error) {
      console.error("Error in getUserSettings:", error);
      return defaultSettings;
    }
  }

  /**
   * Save user settings to Supabase
   * Uses upsert to create or update settings
   */
  async saveUserSettings(
    settings: Partial<UserSettings>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      if (demoService.isDemo()) {
        return { success: true };
      }
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, error: "No authenticated user" };
      }

      // Map our interface to database fields
      const dbSettings = {
        default_transaction_days: settings.default_transaction_days,
        currency: settings.currency,
        date_format: settings.date_format,
        timezone: settings.timezone,
        theme: settings.theme,
        language: settings.language,
        compact_mode: settings.compact_mode,
        expenses_future_months_count: settings.expenses_future_months_count,
        auto_backup: settings.auto_backup,
        data_retention_days: settings.data_retention_days,
        session_timeout: settings.session_timeout,
        require_confirmation: settings.require_confirmation,
        discoverable: settings.discoverable,
        federal_tax_categories: settings.federal_tax_categories,
        state_tax_categories: settings.state_tax_categories,
        custom_transaction_categories: settings.custom_transaction_categories,
        charts_layout: settings.charts_layout,
        wife_value_settings: settings.wife_value_settings,
        household_tasks_tool_settings: settings.household_tasks_tool_settings,
        plaid_environment: settings.plaid_environment,
        enable_ai_categorization: settings.enable_ai_categorization,
        enable_smart_notifications: settings.enable_smart_notifications,
        enable_auto_sync: settings.enable_auto_sync,
      };

      const response = await fetch(`${API_URL}/user-settings`, {
        method: "POST",
        headers,
        body: JSON.stringify(dbSettings),
      });

      if (!response.ok) {
        await handleApiError(response);
        return { success: false, error: "Failed to save settings" };
      }

      // Note: Plaid credentials are managed at the system level
      // The plaid_environment setting controls which mode the system uses
      if (settings.plaid_environment !== undefined) {
        try {
          await fetch("/api/update-settings", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              plaidEnvironment: settings.plaid_environment,
            }),
          });
        } catch (backendError) {
          console.warn(
            "Could not sync Plaid settings with backend:",
            backendError
          );
          // Don't fail the save operation if backend sync fails
        }
      }

      return { success: true };
    } catch (error) {
      console.error("Error in saveUserSettings:", error);
      return { success: false, error: "Failed to save settings" };
    }
  }

  /**
   * Clear user settings (reset to defaults)
   */
  async clearUserSettings(): Promise<{ success: boolean; error?: string }> {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, error: "No authenticated user" };
      }

      const response = await fetch(`${API_URL}/user-settings`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        await handleApiError(response);
        return { success: false, error: "Failed to clear settings" };
      }

      // Clear localStorage as well for immediate effect
      localStorage.removeItem("appSettings");

      return { success: true };
    } catch (error) {
      console.error("Error in clearUserSettings:", error);
      return { success: false, error: "Failed to clear settings" };
    }
  }

  /**
   * Migrate localStorage settings to Supabase (one-time migration)
   */
  async migrateLocalStorageSettings(): Promise<void> {
    try {
      const localSettings = localStorage.getItem("appSettings");
      if (!localSettings) return;
      if (demoService.isDemo()) return;

      const headers = await getAuthHeaders();
      if (!headers.Authorization) return;

      const response = await fetch(`${API_URL}/user-settings`, {
        method: "GET",
        headers,
      });

      if (response.ok) {
        return;
      }

      if (response.status !== 404) {
        await handleApiError(response);
        return;
      }

      const parsed = JSON.parse(localSettings);

      // Map old format to new format
      const settings: Partial<UserSettings> = {
        plaid_environment: parsed.plaidEnvironment || "production",
        default_transaction_days: parsed.defaultTransactionDays,
        currency: parsed.currency,
        date_format: parsed.dateFormat,
        timezone: parsed.timezone,
        theme: parsed.theme,
        language: parsed.language,
        compact_mode: parsed.compactMode,
        auto_backup: parsed.autoBackup,
        data_retention_days: parsed.dataRetentionDays,
        session_timeout: parsed.sessionTimeout,
        require_confirmation: parsed.requireConfirmation,
        discoverable: parsed.discoverable,
        federal_tax_categories: parsed.federalTaxCategories,
        state_tax_categories: parsed.stateTaxCategories,
      };

      await this.saveUserSettings(settings);

      // Clear localStorage after successful migration
      localStorage.removeItem("appSettings");
      console.log("Successfully migrated localStorage settings to Supabase");
    } catch (error) {
      console.error("Error migrating localStorage settings:", error);
    }
  }
}

export const settingsService = new SettingsService();
